// Управление: WASD + PointerLock + ЛКМ; опционально тач-режим (без lock).

export const Btn = {
  Forward: 1 << 0,
  Back:    1 << 1,
  Left:    1 << 2,
  Right:   1 << 3,
  Fire:    1 << 4,
  Jump:    1 << 5,
  Crouch:  1 << 6,
  /** Сервер: та же точка выстрела / прицел, что у chase-камеры (main.ts thirdPerson). */
  ThirdPerson: 1 << 7,
  /** Взаимодействие (дверь и т.д.), сервер: BtnUse — клавиша E. */
  Use: 1 << 8,
} as const;

/** Биты движения — тач-джойстик перезаписывает их целиком (см. setTouchMove). */
export const MOVE_MASK = Btn.Forward | Btn.Back | Btn.Left | Btn.Right;

/** Автоогонь при удержании тач-кнопки: бит Fire пульсирует N тиков вкл / N выкл.
 *  Каждый фронт — «нажатие»; реальный темп по-прежнему режет серверный
 *  FireCooldown (22 тика), так что это лишь замена частых тапов пальцем, а не
 *  ускорение оружия. 3 тика (50 мс) — фронт успевает попасть в окно кулдауна
 *  даже при рассинхроне счётчиков клиента и сервера. */
const TOUCH_FIRE_PULSE_TICKS = 3;

const KEY_MAP: Record<string, number> = {
  KeyW: Btn.Forward, ArrowUp: Btn.Forward,
  KeyS: Btn.Back,    ArrowDown: Btn.Back,
  KeyA: Btn.Left,    ArrowLeft: Btn.Left,
  KeyD: Btn.Right,   ArrowRight: Btn.Right,
  Space: Btn.Jump,
  KeyC: Btn.Crouch,
  KeyE: Btn.Use,
};

// ---- Тач-обзор (телефон). Мышиной чувствительности (Input.sens) НЕ касается. ----
// Все дельты приходят в CSS-пикселях (e.clientX/Y), devicePixelRatio здесь ни при чём.

/** Базовая чувствительность тач-обзора по горизонтали, радиан на CSS-пиксель свайпа.
 *  0.006 рад/px ≈ 0.344°/px: разворот на 180° — свайп ~524 CSS px, то есть ~60–70%
 *  ширины телефона в ландшафте (стандарт мобильных шутеров). Раньше тач брал мышиные
 *  0.0022 (~0.126°/px) — разворот требовал ~1430 px, почти два прохода экраном. */
const TOUCH_LOOK_SENS_X = 0.006;

/** Вертикаль чуть ниже горизонтали (×0.8) — привычно для шутеров:
 *  по высоте нужна точность прицела, а не размах. */
const TOUCH_LOOK_SENS_Y = TOUCH_LOOK_SENS_X * 0.8;

/** Ускорение от скорости свайпа: множитель 1 + k·v, где v — px/мс.
 *  Медленное ведение (~0.1 px/мс) почти не трогает (×1.06) — прицеливание точное;
 *  резкий флик (~2 px/мс) даёт ×2.2 и упирается в кап. */
const TOUCH_LOOK_ACCEL = 0.6;

/** Кап множителя ускорения — быстрее ×2.5 обзор не разгоняется. */
const TOUCH_LOOK_ACCEL_MAX = 2.5;

/** Сглаживание оценки скорости свайпа (EMA, доля нового замера за событие),
 *  чтобы множитель не дрожал от неравномерно приходящих pointer-событий. */
const TOUCH_LOOK_VEL_SMOOTH = 0.4;

export class Input {
  /** Клавиатура + ЛКМ (без тач-кнопок). */
  buttons = 0;
  /** Только экранные кнопки; в sample() объединяется с buttons. */
  touchButtons = 0;
  /** дельта мыши за текущий тик (в радианах после умножения на sens) */
  deltaYaw = 0;
  deltaPitch = 0;
  pitch = 0;
  yaw = 0;

  /** Чувствительность МЫШИ (рад/px, PointerLock). Тач живёт на TOUCH_LOOK_SENS_*. */
  private sens = 0.0022;
  /** weapons: множитель чувствительности обзора (приближение снайперки). */
  lookScale = 1;
  /** Сглаженная скорость текущего свайпа (px/мс) для ускорения тач-обзора. */
  private touchLookVel = 0;
  /** Тач-кнопка огня удерживается (автоогонь пульсами, см. sample()). */
  private touchFireHeld = false;
  /** Счётчик тиков удержания огня — фаза пульса. */
  private touchFireTicks = 0;
  private accumYaw = 0;
  private accumPitch = 0;
  private locked = false;
  private touchMode = false;
  /** One-shot latch для пробела. keydown поднимает в true, `consumeJump()`
   *  сбрасывает в false. Удерживание не вызывает повторных срабатываний —
   *  прыжок запускается только в момент нажатия. */
  private jumpLatched = false;

  private lastMoveHorizontalCode: string | null = null;
  private lastMoveVerticalCode: string | null = null;

  constructor(private canvas: HTMLCanvasElement) {
    window.addEventListener("keydown", this.onKey, { passive: true });
    window.addEventListener("keyup", this.onKey, { passive: true });
    window.addEventListener("mousedown", this.onMouse, { passive: true });
    window.addEventListener("mouseup", this.onMouse, { passive: true });
    document.addEventListener("pointerlockchange", () => {
      const wasLocked = this.locked;
      this.locked = document.pointerLockElement === this.canvas;
      this.accumYaw = 0;
      this.accumPitch = 0;
      if (wasLocked && !this.locked) {
        this.buttons = 0;
        this.touchButtons = 0;
        this.jumpLatched = false;
        this.lastMoveHorizontalCode = null;
        this.lastMoveVerticalCode = null;
      }
    });
    window.addEventListener("blur", () => {
      this.buttons = 0;
      this.touchButtons = 0;
      this.touchFireHeld = false;
      this.jumpLatched = false;
      this.lastMoveHorizontalCode = null;
      this.lastMoveVerticalCode = null;
    });
    document.addEventListener("mousemove", (ev) => {
      if (this.touchMode || !this.locked) return;
      this.accumYaw   -= ev.movementX * this.sens * this.lookScale;
      this.accumPitch -= ev.movementY * this.sens * this.lookScale;
    });
    canvas.addEventListener("click", () => {
      if (!this.touchMode && !this.locked) this.requestLock();
    });
  }

  setTouchMode(on: boolean): void {
    this.touchMode = on;
    if (on) {
      this.accumYaw = 0;
      this.accumPitch = 0;
    }
  }

  getTouchMode(): boolean {
    return this.touchMode;
  }

  /** Начало нового свайпа (pointerdown на зоне обзора): сброс оценки скорости,
   *  чтобы хвост прошлого флика не ускорял первое же движение нового. */
  beginTouchLook(): void {
    this.touchLookVel = 0;
  }

  /** Дельта свайпа в CSS-пикселях (из e.clientX/Y); dtMs — время с прошлого события
   *  той же серии, для оценки скорости. Быстрый свайп поворачивает дополнительно
   *  быстрее (до ×TOUCH_LOOK_ACCEL_MAX), медленное ведение остаётся точным. */
  addTouchLook(dxPx: number, dyPx: number, dtMs = 16.7): void {
    const dt = Math.max(dtMs, 1); // защита от dt≈0 у слипшихся pointer-событий
    const v = Math.hypot(dxPx, dyPx) / dt;
    this.touchLookVel += (v - this.touchLookVel) * TOUCH_LOOK_VEL_SMOOTH;
    const boost = Math.min(1 + TOUCH_LOOK_ACCEL * this.touchLookVel, TOUCH_LOOK_ACCEL_MAX);
    this.accumYaw   -= dxPx * TOUCH_LOOK_SENS_X * boost * this.lookScale;
    this.accumPitch -= dyPx * TOUCH_LOOK_SENS_Y * boost * this.lookScale;
  }

  setTouchButton(mask: number, down: boolean): void {
    if (down) this.touchButtons |= mask;
    else this.touchButtons &= ~mask;
  }

  /** Джойстик: заменить биты движения целиком (0 = стоим). Противоположные
   *  направления джойстик никогда не даёт, resolveOpposingMovement не нужен. */
  setTouchMove(mask: number): void {
    this.touchButtons = (this.touchButtons & ~MOVE_MASK) | (mask & MOVE_MASK);
  }

  /** Удержание тач-кнопки огня. Первый sample() после нажатия даёт фронт сразу
   *  (мгновенный выстрел), дальше — пульсы TOUCH_FIRE_PULSE_TICKS. */
  setTouchFireHeld(down: boolean): void {
    if (down && !this.touchFireHeld) this.touchFireTicks = 0;
    this.touchFireHeld = down;
  }

  clearTouchState(): void {
    this.touchButtons = 0;
    this.touchFireHeld = false;
    this.touchFireTicks = 0;
    this.accumYaw = 0;
    this.accumPitch = 0;
  }

  /** Установить yaw извне (например, на спавне с сервера). */
  setYaw(y: number): void {
    this.yaw = y;
    this.accumYaw = 0;
  }

  requestLock(): void {
    if (this.touchMode) return;
    const p = this.canvas.requestPointerLock() as unknown;
    if (p && typeof (p as Promise<void>).then === "function") {
      (p as Promise<void>).catch(() => { /* user gesture может быть просрочен */ });
    }
  }

  /** true если с последнего вызова `consumeJump()` было нажатие Space.
   *  Потребляется (сбрасывается в false) при каждом вызове. */
  consumeJump(): boolean {
    const v = this.jumpLatched;
    this.jumpLatched = false;
    return v;
  }

  /** Вызывать раз за фикс-тик. Вернёт текущий снимок и сбросит дельты. */
  sample(): { buttons: number; yaw: number; pitch: number } {
    this.yaw = (this.yaw + this.accumYaw) % (Math.PI * 2);
    this.pitch = clamp(this.pitch + this.accumPitch, -1.2, 1.2);
    this.deltaYaw = this.accumYaw;
    this.deltaPitch = this.accumPitch;
    this.accumYaw = 0;
    this.accumPitch = 0;
    let merged = this.buttons | this.touchButtons;
    if (this.touchFireHeld) {
      // Пульс: вкл на тиках [0,N), выкл на [N,2N), снова вкл… — рождает фронты.
      const phaseOn = Math.floor(this.touchFireTicks / TOUCH_FIRE_PULSE_TICKS) % 2 === 0;
      if (phaseOn) merged |= Btn.Fire;
      this.touchFireTicks++;
    }
    merged = this.resolveOpposingMovement(merged);
    return { buttons: merged, yaw: this.yaw, pitch: this.pitch };
  }

  /** Тач-кнопки: перед pointerdown вызвать с кодом как у KeyboardEvent (KeyW, KeyA, …),
   *  чтобы при конфликте влево/вправо побеждало последнее действие. */
  recordMovementKeyDown(evCode: string): void {
    const m = KEY_MAP[evCode];
    if (!m) return;
    if ((m & (Btn.Left | Btn.Right)) !== 0) this.lastMoveHorizontalCode = evCode;
    if ((m & (Btn.Forward | Btn.Back)) !== 0) this.lastMoveVerticalCode = evCode;
  }

  private resolveOpposingMovement(b: number): number {
    const left = (b & Btn.Left) !== 0;
    const right = (b & Btn.Right) !== 0;
    if (left && right) {
      const leftCodes = new Set(["KeyA", "ArrowLeft"]);
      const preferLeft =
        this.lastMoveHorizontalCode != null &&
        leftCodes.has(this.lastMoveHorizontalCode);
      if (preferLeft) return b & ~Btn.Right;
      return b & ~Btn.Left;
    }
    const fwd = (b & Btn.Forward) !== 0;
    const back = (b & Btn.Back) !== 0;
    if (fwd && back) {
      const fwdCodes = new Set(["KeyW", "ArrowUp"]);
      const preferFwd =
        this.lastMoveVerticalCode != null &&
        fwdCodes.has(this.lastMoveVerticalCode);
      if (preferFwd) return b & ~Btn.Back;
      return b & ~Btn.Forward;
    }
    return b;
  }

  private onKey = (ev: KeyboardEvent): void => {
    const m = KEY_MAP[ev.code];
    if (!m) return;
    if (ev.type === "keydown") {
      if (!ev.repeat) {
        if ((m & (Btn.Left | Btn.Right)) !== 0) this.lastMoveHorizontalCode = ev.code;
        if ((m & (Btn.Forward | Btn.Back)) !== 0) this.lastMoveVerticalCode = ev.code;
      }
      if (ev.code === "Space" && (this.buttons & Btn.Jump) === 0) {
        this.jumpLatched = true;
      }
      this.buttons |= m;
    } else {
      this.buttons &= ~m;
    }
  };

  private onMouse = (ev: MouseEvent): void => {
    if (ev.button !== 0) return;
    // Огонь только при захвате курсора canvas — иначе ЛКМ по меню даёт «выстрел».
    if (document.pointerLockElement !== this.canvas) return;
    if (ev.type === "mousedown") this.buttons |= Btn.Fire;
    else this.buttons &= ~Btn.Fire;
  };
}

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}
